import React, { useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import { Button, Collapse, Divider, ListItem } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@emotion/react";
import { icons } from "lucide-react";
import { SquareArrowOutUpRight } from "lucide-react-new";

const useStyles = makeStyles()((theme, _params, classes) => ({
    item: {
        display: "block",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: "8px",
    },
    itemLeaf: {
        display: "flex",
        padding: "0 8px",
        margin: "10px 8px",
        width: "220px",
        color: "#A3ACA9",
        position: "relative",
        "&:hover": {
            color: "#A3ACA9",
            backgroundColor: "#232525",
            height: "36px",
            width: "212px",
            borderRadius: "4px",
            paddingRight: 0,
        },
        [`&:hover .${classes.icon}`]: {
            color: "#A3ACA9",
        },
        [`&:focus .${classes.icon}`]: {
            color: "#35B797",
        },
        [`&.active .${classes.icon}`]: {
            color: "#35B797",
        },
    },
    verticalItemLeaf: {
        display: "flex",
        padding: "0 8px",
        margin: "0 0 0 14px",
        width: "218px",
        color: "#303432",
        position: "relative",
        "&:before": {
            content: '""',
            position: "absolute",
            left: "22px",
            borderLeft: "2px solid #303432",
            width: "1px",
        },

        "&:first-of-type:before": {
            top: "22px",
            height: "28px",
        },
        "&:last-child:before": {
            height: "28px",
            top: "-14px",
        },
    },
    button: {
        fontFamily: "Plus Jakarta Sans",
        color: "#A3ACA9",
        padding: "8px 8px 8px 0",
        marginLeft: "8px",
        justifyContent: "flex-start",
        textTransform: "none",
        letterSpacing: 0,
        height: "36px",
        width: "212px",
        "&:hover": {
            color: "#E9EBEA",
            backgroundColor: "#232525",
            height: "36px",
            width: "212px",
        },
        [`&:hover .${classes.icon}`]: {
            color: "#A3ACA9",
        },
    },

    metalButtonLeaf: {
        fontFamily: "Plus Jakarta Sans",
        color: "#A3ACA9",
        padding: "8px 8px 8px 0",
        justifyContent: "flex-start",
        textTransform: "none",
        height: "36px",
        letterSpacing: 0,
        width: "220px",
        fontWeight: "600",
        fontSynthesis: "none",
        "&:hover": {
            color: "#E9EBEA",
            backgroundColor: "#232525",
            height: "36px",
            width: "212px",
        },
        [`&:hover .${classes.icon}`]: {
            color: "#A3ACA9",
        },
        [`&.active .${classes.icon}`]: {
            color: "#35B797",
        },
        [`&.active .${classes.title}`]: {
            color: "#E9EBEA",
        },
        [`&:hover .${classes.openInNewWindow}`]: {
            display: "flex",
            marginRight: "2px",
        },
        "&.depth-0": {
            [`& .${classes.title}`]: {
                fontWeight: "600",
                fontSynthesis: "none",
            },
        },
    },
    verticalMetalButtonLeaf: {
        fontFamily: "Plus Jakarta Sans",
        color: "#A3ACA9",
        padding: "8px 8px 8px 0",
        justifyContent: "flex-start",
        textTransform: "none",
        height: "36px",
        letterSpacing: 0,
        width: "220px",
        fontWeight: "600",
        fontSynthesis: "none",
        [`& .${classes.title}`]: {
            paddingLeft: "8px",
        },
        "&:hover": {
            color: "#E9EBEA",
            backgroundColor: "transparent",
            height: "36px",
            width: "198px",
            paddingRight: "0",
        },
        [`&:hover .${classes.title}`]: {
            color: "#E9EBEA",
            backgroundColor: "#232525",
            height: "36px",
            width: "220px",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
            paddingLeft: "8px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
        },
        [`&:hover .${classes.openInNewWindow}`]: {
            display: "flex",
            backgroundColor: "#232525",
            height: "36px",
            width: "35.5px",
            borderTopRightRadius: "4px",
            borderBottomRightRadius: "4px",
            paddingRight: "9px",
        },
    },
    metalButtonLeafActive: {
        fontFamily: "Plus Jakarta Sans",
        color: "#E9EBEA",
        fontWeight: "600",
        fontSynthesis: "none",
        padding: "8px 8px 8px 0",
        justifyContent: "flex-start",
        textTransform: "none",
        height: "36px",
        letterSpacing: 0,
        width: "100%",
        "&:hover": {
            color: "#E9EBEA",
            backgroundColor: "transparent",
            height: "36px",
            width: "212px",
            paddingRight: "0",
        },
        [`&:hover .${classes.title}`]: {
            color: "#E9EBEA",
            backgroundColor: "#232525",
            height: "36px",
            width: "180px",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
            paddingLeft: "8px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
        },
        [`&:hover .${classes.icon}`]: {
            color: "#A3ACA9",
        },
        [`&:focus .${classes.icon}`]: {
            color: "#35B797",
        },
        [`&.active .${classes.icon}`]: {
            color: "#35B797",
        },

        [`& .${classes.icon}`]: {
            color: "#35B797",
        },
        "&.depth-0": {
            [`& .${classes.title}`]: {
                fontWeight: "600",
                fontSynthesis: "none",
            },
        },
    },
    verticalMetalButtonLeafActive: {
        fontFamily: "Plus Jakarta Sans",
        color: "#E9EBEA",
        fontSynthesis: "none",
        fontWeight: "600",
        padding: "8px 8px 8px 0",
        justifyContent: "flex-start",
        textTransform: "none",
        height: "36px",
        letterSpacing: 0,
        width: "100%",
        "&:hover": {
            color: "#E9EBEA",
            backgroundColor: "transparent",
            height: "36px",
            width: "198px",
            paddingRight: 0,
        },
        [`&:hover .${classes.title}`]: {
            color: "#E9EBEA",
            backgroundColor: "#232525",
            height: "36px",
            width: "180px",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
            paddingLeft: "8px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            borderRadius: "4px",
        },
        [`&:hover .${classes.icon}`]: {
            color: "#A3ACA9",
        },
        [`&:focus .${classes.icon}`]: {
            color: "#35B797",
        },
        [`&.active .${classes.icon}`]: {
            color: "#35B797",
        },

        [`& .${classes.icon}`]: {
            color: "#35B797",
        },
        [`& .${classes.title}`]: {
            fontSynthesis: "none",
            fontWeight: "600",
            paddingLeft: "8px",
        },
    },
    icon: {
        display: "flex",
        alignItems: "center",
        marginLeft: theme.spacing(1.5),
        marginRight: theme.spacing(1.5),
        minWidth: 20,
        color: "#A3ACA9",
    },
    title: {
        marginRight: "auto",
    },
    openInNewWindow: {
        display: "none",
    },
    vertical: {
        display: "flex",
        alignItems: "center",
        minWidth: 30,
        color: "#303432",
    },
}));

const NavItem = ({
    children,
    className,
    depth,
    href,
    target,
    redirect,
    icon: Icon,
    info: Info,
    open: openProp,
    title,
    pendingRequestsLength,
    activeAlertsCount,
    ...rest
}) => {
    const { classes, cx } = useStyles();
    const [open, setOpen] = useState(openProp);
    const theme = useTheme();
    const LucideIcon = icons[Icon || "Dot"];
    const ChevronUp = icons["ChevronUp"];
    const ChevronDown = icons["ChevronDown"];
    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    let paddingLeft = 8;

    if (depth > 0) {
        paddingLeft = 32 + 8 * depth;
    }

    const style = { paddingLeft };

    if (children) {
        return (
            <ListItem className={cx(classes.item, className)} disableGutters key={title} {...rest}>
                <Button className={classes.button} onClick={handleToggle} style={style}>
                    <LucideIcon className={classes.icon} size={20} />
                    <span className={classes.title} style={open ? { color: "#E9EBEA" } : {}}>
                        {title}
                    </span>
                    {open ? (
                        <ChevronUp size={20} style={open ? { color: "#E9EBEA" } : {}} />
                    ) : (
                        <ChevronDown size={20} />
                    )}
                </Button>
                <Collapse in={open}>{children}</Collapse>
                {title === "Help" && <Divider variant="middle" style={{ paddingTop: 10 }} />}
            </ListItem>
        );
    }

    return (
        <ListItem
            className={
                LucideIcon.displayName === "Dot"
                    ? cx(classes.verticalItemLeaf, className)
                    : cx(classes.itemLeaf, className)
            }
            disableGutters
            key={title}
            {...rest}
        >
            <Button
                component={RouterLink}
                className={
                    LucideIcon.displayName === "Dot" && href === location.pathname
                        ? cx(classes.verticalMetalButtonLeafActive, `depth-${depth}`)
                        : !LucideIcon.displayName === "Dot" && href === location.pathname
                        ? cx(classes.metalButtonLeafActive, `depth-${depth}`)
                        : LucideIcon.displayName === "Dot" && href !== location.pathname
                        ? cx(classes.verticalMetalButtonLeaf, `depth-${depth}`)
                        : cx(classes.metalButtonLeaf, `depth-${depth}`)
                }
                to={href}
                target={target}
            >
                <LucideIcon
                    className={
                        LucideIcon.displayName === "Dot" ? `${classes.vertical}` : `${classes.icon}`
                    }
                    size={LucideIcon.displayName === "Dot" ? 30 : 20}
                />
                <span className={classes.title}>{title}</span>
                {Info && <Info className={classes.info} />}
                {title === "Approvals" &&
                    pendingRequestsLength !== null &&
                    pendingRequestsLength !== 0 && (
                        <div
                            className="m-chip m-alert"
                            data-color-status="warning"
                            style={{
                                cursor: "pointer",
                                fontSize: "var(--font-size-xs)",
                                paddingTop: 14,
                                paddingBottom: 14,
                            }}
                        >
                            {pendingRequestsLength}
                        </div>
                    )}
                {title === "Alerts" && activeAlertsCount !== null && activeAlertsCount !== 0 && (
                    <div
                        className="m-chip m-alert"
                        data-color-status="warning"
                        style={{
                            cursor: "pointer",
                            fontSize: "var(--font-size-xs)",
                            paddingTop: 14,
                            paddingBottom: 14,
                        }}
                    >
                        {activeAlertsCount}
                    </div>
                )}
                <SquareArrowOutUpRight
                    size={16}
                    style={target || redirect ? {} : { display: "none" }}
                    className={classes.openInNewWindow}
                />
            </Button>
        </ListItem>
    );
};

NavItem.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    depth: PropTypes.number.isRequired,
    href: PropTypes.string,
    target: PropTypes.string,
    icon: PropTypes.string,
    info: PropTypes.elementType,
    open: PropTypes.bool,
    title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
    open: false,
};

export default NavItem;
